import { GraduationCap, Home, Users, Building } from 'lucide-react';
import FeatureDetailPage from '../../components/marketing/FeatureDetailPage';
import { useLanguage } from '../../contexts/LanguageContext';

const SchoolsUseCase = () => {
  const { tr } = useLanguage();
  const relatedFeatures = [
    { icon: GraduationCap, iconColor: 'bg-purple-100 text-purple-600', titleKey: 'useCaseClassroomTitle', descKey: 'useCaseClassroomDesc', link: '/use-cases/classroom-teachers' },
    { icon: Home, iconColor: 'bg-teal-100 text-teal-600', titleKey: 'useCaseHomeschoolTitle', descKey: 'useCaseHomeschoolDesc', link: '/use-cases/homeschool' },
    { icon: Users, iconColor: 'bg-amber-100 text-amber-600', titleKey: 'useCaseTutorsTitle', descKey: 'useCaseTutorsDesc', link: '/use-cases/tutors' }
  ];

  const pillars = [
    {
      title: tr('One consistent platform for every classroom', 'Una plataforma consistente para cada salón'),
      desc: tr('Give every teacher the same lesson planner, gradebook, attendance tracker and AI tools. New teachers onboard in minutes instead of weeks, substitutes get ready-made packets, and curriculum stays consistent across grade levels.', 'Dale a cada maestro el mismo planificador de lecciones, registro de notas, control de asistencia y herramientas de IA. Los maestros nuevos se integran en minutos en lugar de semanas, los sustitutos reciben paquetes listos, y el currículo se mantiene consistente entre grados.')
    },
    {
      title: tr('Standards alignment built in', 'Alineación con estándares incorporada'),
      desc: tr('Every AI-generated lesson, quiz and worksheet is aligned to Common Core and Puerto Rico Core standards, with bilingual English/Spanish support and ESL adaptations following WIDA guidelines — ideal for diverse student populations.', 'Cada lección, quiz y hoja de trabajo generada con IA está alineada a los estándares Common Core y de Puerto Rico, con soporte bilingüe inglés/español y adaptaciones ESL siguiendo las guías WIDA, ideal para poblaciones estudiantiles diversas.')
    },
    {
      title: tr('Administrative visibility without micromanaging', 'Visibilidad administrativa sin microgestión'),
      desc: tr('School administrators see attendance trends, grading progress and intervention flags across classrooms from a single dashboard, while teachers keep full ownership of their day-to-day planning.', 'Los administradores escolares ven tendencias de asistencia, progreso de calificaciones y alertas de intervención en todos los salones desde un solo panel, mientras los maestros mantienen el control total de su planificación diaria.')
    },
    {
      title: tr('Student data protection you can document', 'Protección de datos estudiantiles que puedes documentar'),
      desc: tr('FERPA and COPPA aligned practices, role-based access, school-level data isolation, signed DPAs and a full Trust Center with security documentation your district can review before adoption.', 'Prácticas alineadas con FERPA y COPPA, acceso basado en roles, aislamiento de datos por escuela, DPAs firmados y un Centro de Confianza completo con documentación de seguridad que tu distrito puede revisar antes de la adopción.')
    }
  ];

  const faqs = [
    {
      q: tr('How does pricing work for schools?', '¿Cómo funciona el precio para escuelas?'),
      a: tr('Schools receive volume pricing based on the number of teachers. Every teacher gets full access to all features, and administrators get oversight dashboards at no extra cost. Contact us for a quote tailored to your school size.', 'Las escuelas reciben precios por volumen según el número de maestros. Cada maestro obtiene acceso completo a todas las funciones, y los administradores obtienen paneles de supervisión sin costo adicional. Contáctanos para una cotización según el tamaño de tu escuela.')
    },
    {
      q: tr('Can we roll it out gradually?', '¿Podemos implementarlo gradualmente?'),
      a: tr('Yes. Many schools start with one grade level or department, then expand. Teacher accounts are independent, so a phased rollout requires no special setup — each teacher simply joins the school workspace when ready.', 'Sí. Muchas escuelas comienzan con un grado o departamento y luego se expanden. Las cuentas de maestros son independientes, así que una implementación por fases no requiere configuración especial: cada maestro se une al espacio de la escuela cuando esté listo.')
    },
    {
      q: tr('Does it support bilingual schools?', '¿Soporta escuelas bilingües?'),
      a: tr('Fully. The entire interface is available in English, Spanish, French, German, Italian, Portuguese and Chinese, and AI materials can be generated in English or Spanish — plus ESL-adapted content for English learners.', 'Totalmente. Toda la interfaz está disponible en inglés, español, francés, alemán, italiano, portugués y chino, y los materiales de IA pueden generarse en inglés o español, además de contenido adaptado a ESL para estudiantes de inglés.')
    },
    {
      q: tr('What about parent communication?', '¿Qué hay de la comunicación con padres?'),
      a: tr('Teachers share progress through secure parent portals and AI-drafted parent emails built from real classroom data. Schools can include their logo and maintain a consistent communication standard across classrooms.', 'Los maestros comparten el progreso mediante portales seguros para padres y correos redactados con IA a partir de datos reales del salón. Las escuelas pueden incluir su logo y mantener un estándar de comunicación consistente entre salones.')
    }
  ];

  return (
    <FeatureDetailPage
      featureKey="Schools"
      icon={Building}
      iconColor="bg-blue-100 text-blue-600"
      heroImage="https://static.prod-images.emergentagent.com/jobs/9651e3ca-5bf4-4d9b-b045-1d41a1fb3908/images/c8be25d87aab69a4e573fdc04883804ea754936479aada9258afdf675dfd031f.png"
      relatedFeatures={relatedFeatures}
    >
      {/* School pillars */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 sm:mb-10 text-center">
            {tr('Why schools standardize on TeacherHubPro', 'Por qué las escuelas se estandarizan en TeacherHubPro')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {pillars.map((p, i) => (
              <div key={i} className="bg-blue-50/60 border border-blue-100 rounded-xl p-5 sm:p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">
            {tr('Frequently asked questions from school leaders', 'Preguntas frecuentes de líderes escolares')}
          </h2>
          <div className="space-y-5">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{f.q}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </FeatureDetailPage>
  );
};

export default SchoolsUseCase;
